import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { withRouter, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Divider from '@material-ui/core/Divider';
import Footer from '../../Components/Layout/Footer';
import Routes from '../../Routing/Routes';
import Seo from '../../Routing/Seo';

import LayoutStyles from '../../Components/Layout/Styles/LayoutStyles';

type Props = {
  classes: Object,
};

const styles = () => ({
  layout: {
    ...LayoutStyles.centered800Layout,
    marginTop: '100px',
  },
});

function Cgu({ classes }: Props) {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        {Seo.title(
          `Conditions générales d'utilisations${t(`${Routes.currentNamespace()}.SEO.SUFFIX`)}`,
        )}
        {Seo.description("Nos conditions générales d'utilisations")}
        {Seo.canonical(null, Routes.cguUrl())}
      </Helmet>
      <Grid container className={classes.container}>
        <div className={classes.layout}>
          <Typography
            variant="h1"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: t('WELLO.CGU.TITLE'),
            }}
          />

          <Typography
            variant="subtitle1"
            component="h2"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: 'En date du 19 septembre 2023',
            }}
          />

          <Divider light style={{ margin: '20px', background: 'none' }} />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: 'Préambule : Définitions',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>La présente clause a pour objet de définir les différents termes essentiels du contrat :<ul><li>CGU : désigne les présentes conditions générales d’utilisation applicables à la Plateforme.</li><li>Compte : désigne  l’espace personnel regroupant l’ensemble des droits attribués à un Utilisateur, qu’il doit créer pour pouvoir devenir Membre et accéder à certains Services de la Plateforme.</li><li>Éditeur : désigne la société Wello, SAS au capital de 10 000 €, immatriculée au Registre du Commerce et des Sociétés de Nantes sous le numéro 831 768 783, dont le siège social est situé 5 place Jean Ligonday, 44610 Indre.</li><li>Membre : l’Utilisateur devient Membre lorsqu’il est identifié sur la Plateforme.</li><li>Plateforme : désigne le site internet accessible à l’URL www.aidant-ma-vie.fr. La Plateforme est gérée par un administrateur de Plateforme.</li><li>Rôle : désigne la relation entre un Membre et une Structure. Le Rôle définit les droits et les accès du Membre dans sa Structure, il est soumis à la validation de l’administrateur de la Structure. Les principaux Rôles des Membres d’une Structure sont les suivants : bénéficiaires et professionnels.</li><li>Services : désigne les Services mis à disposition des Utilisateurs via la Plateforme. Certains Services sont réservés aux Membres ou à certains Rôles.</li><li>Structure : désigne une organisation (association, entreprise, etc.) référencée sur la Plateforme. Chaque Structure est administrée par un administrateur de Structure.</li><li>Utilisateur : désigne toute personne qui utilise la Plateforme et accède aux Services proposés par la Plateforme.</li></ul></p>',
            }}
          />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: '1. Objet de la Plateforme',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>La Plateforme a pour objectif d’apporter une réponse aux besoins émergents des personnes en situation de handicap et de leurs aidants dans le domaine du bien-être, du sport et de la santé : informations, orientation, services et activités proposés et encadrés par les professionnels du Centre Hélène Borel.</p>',
            }}
          />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: '2. Acceptation des Conditions Générales d’Utilisation',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>Les présentes CGU ont pour objet l’encadrement juridique des modalités de mise à disposition des Services de la Plateforme aux Utilisateurs.</p><p>Les CGU doivent être acceptées par tout Utilisateur, et son accès à la Plateforme vaut acceptation de ces conditions.</p><p>Lors de la création de Compte pour devenir Membre de la Plateforme, l’Utilisateur doit impérativement approuver les CGU, via une case à cocher.</p><p>La Plateforme se réserve le droit de modifier unilatéralement et à tout moment le contenu des présentes CGU.</p>',
            }}
          />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: '3. Accès aux Services',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>L’Utilisateur de la Plateforme a accès aux Services suivants :<ul><li>Prise de contact avec une Structure</li><li>Accès à certains contenus non réservés aux Membres</li></ul></p><p>Le Membre de la Plateforme a accès aux Services suivants :<ul><li>Le paramétrage de son profil</li><li>La demande de services en ligne</li><li>La demande d’inscription à une activité</li><li>L’accès aux contenus et fonctionnalités réservés aux Membres</li></ul></p><p>Selon les droits et habilitations attribués par l’administrateur de Structure, le Membre dont le Rôle est “professionnel” a accès aux Services suivants :<ul><li>L’accès aux fonctionnalités backoffice de sa Structure<li>La consultation des demandes en ligne effectuées par les bénéficiaires</li><li>La mise en ligne d’activités dans le planning d’activités de sa Structure</li><li>La gestion des inscriptions aux activités</li><li>La publication de contenus dans l’espace documentaire de sa Structure</li></ul></p><p>L’administrateur de Structure décide des contenus et Services accessibles par tous les Utilisateurs ou réservés aux Membres ou à certains Rôles.</br>Il peut mettre à jour sa présentation, ses coordonnées, ses contenus, créer des documents et gérer son planning d’activités.</p><p><b>Création de Compte de Membre et attribution d’un Rôle</b></br>Un Utilisateur peut créer son Compte, définir ses identifiants (adresse électronique et mot de passe confidentiel) et ainsi devenir Membre :<ul><li>lorsqu’il souhaite accéder à certains contenus ou certains Services réservés aux Membres d’une Structure. L’intégration du Membre à la Structure est soumise à la validation de l’administrateur de la Structure.</li><li>lorsqu’il est invité par mail par un administrateur de Structure ou de la Plateforme.</li></ul></p><p>Au sein d’une Structure, la gestion des droits et habilitations attribués aux Membres est effectuée par l’administrateur de la Structure qui attribue un Rôle à chaque Membre.</br>Lorsqu’un Utilisateur demande à rejoindre une Structure, le Rôle de bénéficiaire lui est attribué par défaut. Ce Rôle est modifiable par l’administrateur de la Structure.</br>Lors de l’invitation d’un Membre dans une Structure par l’administrateur de la Structure, un Rôle est attribué au Membre (bénéficiaire ou professionnel).</p><p><b>Conditions d’accès</b></br>Dans la présente version, la Plateforme est accessible gratuitement en tout lieu à tout Utilisateur ayant un accès à Internet. Tous les frais supportés par l’Utilisateur pour accéder au service (matériel informatique, logiciels, connexion Internet, etc.) sont à sa charge.</p><p>La Plateforme met en œuvre tous les moyens mis à sa disposition pour assurer un accès de qualité à ses services. L’obligation étant de moyens, la Plateforme ne s’engage pas à atteindre ce résultat.</p><p>Tout événement dû à un cas de force majeure ayant pour conséquence un dysfonctionnement du réseau ou du serveur n’engage pas la responsabilité de l’Éditeur.</p><p>La Plateforme et ses différents Services peuvent être interrompus ou suspendus par l’éditeur, notamment à l’occasion d’une maintenance, sans obligation de préavis ou de justification.</p><p>L’Utilisateur a la possibilité de contacter la Plateforme par messagerie électronique à l’adresse pfs@centre-helene-borel.com.</p>',
            }}
          />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: '4. Propriété intellectuelle',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>La Plateforme est protégée au titre de la propriété intellectuelle.</p><p>Tous les programmes informatiques, bases de données, ainsi que toutes les mises à jour, nouvelles versions et copies desdits programmes mis à disposition des Utilisateurs au travers de la Plateforme sont et resteront la propriété de l’Éditeur.</p><p>Tous les éléments accessibles sur la Plateforme (nom de domaine, textes, images, icônes, sons, vidéos, données, etc) sont protégés au titre de la propriété intellectuelle, ils sont la propriété du Centre Hélène Borel, à l’exception du contenu fourni ou posté par les Utilisateurs ou les Membres</p><p>Toute représentation ou reproduction par quelque procédé que ce soit, sans l’accord préalable de l’Administrateur de Plateforme et de l’Éditeur constitue donc un acte de contrefaçon sanctionné et réprimé par les dispositions des articles L.335-2 et suivants du Code de la propriété intellectuelle.</p><p>L’Utilisateur ou le Membre devra solliciter l’autorisation préalable de la Plateforme pour toute reproduction, publication, copie des différents contenus.</p><p>L’Utilisateur ou le Membre s’engage à une utilisation des contenus de la Plateforme dans un cadre strictement privé. Une utilisation des contenus à des fins commerciales est strictement interdite.Tout contenu mis en ligne par un Membre est de sa seule responsabilité. Le Membre s’engage à ne pas mettre en ligne de contenus pouvant porter atteinte aux intérêts de tierces personnes. Tout recours en justice engagé par un tiers lésé contre la Plateforme sera pris en charge par l’Utilisateur.</p>',
            }}
          />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: '5. Liens hypertextes',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>Des liens hypertextes sortants peuvent être sur la Plateforme, cependant les pages web où mènent ces liens n’engagent en rien la responsabilité du Centre Hélène Borel qui n’a pas le contrôle de ces liens.</p><p>Si un internaute ou une personne morale désire créer, à partir de son site, un lien hypertexte vers la Plateforme, il doit préalablement solliciter l’accord de l’administrateur de Plateforme par mail à pfs@centre-helene-borel.com.</p>',
            }}
          />

          <Typography
            variant="h4"
            gutterBottom
            dangerouslySetInnerHTML={{
              __html: '6. Règles régissant les conditions générales d’utilisation',
            }}
          />

          <Typography
            gutterBottom
            dangerouslySetInnerHTML={{
              __html:
                '<p>Évolution du contrat : La Plateforme se réserve à tout moment le droit de modifier les clauses stipulées dans le présent contrat.</br>Durée : La durée du présent contrat est indéterminée. Le contrat produit ses effets à l’égard de l’Utilisateur à compter de l’utilisation du service.</br>Droit applicable et juridiction compétente : La législation française s’applique au présent contrat. En cas d’absence de résolution amiable d’un litige né entre les parties, seuls les tribunaux français sont compétents.</p>',
            }}
          />

          <Grid item md={12} style={{ padding: '60px' }} align="middle">
            {/* <BackButton /> */}
            <Button component={Link} to="/">
              <Typography
                variant="subtitle2"
                color="textPrimary"
                component="p"
                dangerouslySetInnerHTML={{
                  __html: t('BACK'),
                }}
              />
            </Button>
          </Grid>
        </div>
      </Grid>
      <Footer />
    </>
  );
}

export default withRouter(withStyles(styles)(Cgu));
